/**
 * Add Domain Modal Component
 * Form for adding a new whitelisted domain
 */

import { useState, FormEvent } from 'react';
import type { CreateDomainRequest } from '../../../types/access-control';

interface AddDomainModalProps {
  onClose: (saved: boolean) => void;
  onSubmit: (data: CreateDomainRequest) => Promise<void>;
}

export function AddDomainModal({ onClose, onSubmit }: AddDomainModalProps) {
  const [formData, setFormData] = useState<CreateDomainRequest>({
    domain: '',
    autoApprove: false,
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validate domain format
  const validateDomain = (domain: string): boolean => {
    // Basic domain validation regex
    const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;
    return domainRegex.test(domain);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.domain.trim()) {
      setError('Domain is required');
      return;
    }

    if (!validateDomain(formData.domain.trim())) {
      setError('Invalid domain format (e.g., example.com)');
      return;
    }

    setLoading(true);

    try {
      const request: CreateDomainRequest = {
        domain: formData.domain.trim().toLowerCase(),
      };
      if (formData.autoApprove !== undefined) request.autoApprove = formData.autoApprove;
      const desc = formData.description?.trim();
      if (desc) request.description = desc;

      await onSubmit(request);
      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add domain');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-bold text-gray-900">Add Whitelisted Domain</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          {/* Domain Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domain <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="example.com"
              required
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the domain name (e.g., company.com). Users with email addresses from this domain will be eligible for access.
            </p>
          </div>

          {/* Auto-Approve Checkbox */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="autoApprove"
              checked={formData.autoApprove}
              onChange={(e) => setFormData({ ...formData, autoApprove: e.target.checked })}
              className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              disabled={loading}
            />
            <label htmlFor="autoApprove" className="ml-2 block text-sm text-gray-700">
              <span className="font-medium">Auto-approve requests</span>
              <p className="text-xs text-gray-500 mt-1">
                Automatically approve access requests from users with this domain
              </p>
            </label>
          </div>

          {/* Description Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows={3}
              placeholder="Optional notes about this domain..."
              disabled={loading}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Domain'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
